import type { ReactNode } from 'react';
import type { Loop } from '@/common/api/types/loops';
import type { ObjectiveBucket } from '@/features/agentic-insights/components/experimental/shared/data/objectiveBuckets';
import type { AggregateLoopRun } from '../data/aggregateFindings';
import type { LoopScope } from '../data/loopScope';
import { TONE, TYPE } from '../language';
import { LoopSwitcher } from './LoopSwitcher';
import { ObjectiveFilterOverflow } from './ObjectiveFilterOverflow';

interface NavigationBarProps {
  scope: LoopScope;
  navLoopId: number;
  loops: readonly Loop[];
  runsByLoopId: ReadonlyMap<number, AggregateLoopRun>;
  totalLoopFindings: number;
  onScopeChange: (scope: LoopScope) => void;
  buckets: ObjectiveBucket[];
  totalFindings: number;
  objective: string | undefined;
  onObjectiveChange: (objective: string | undefined) => void;
  /** Told whenever the loop switcher's popover opens or closes — see `LoopSwitcher`. */
  onLoopSwitcherOpenChange?: (open: boolean) => void;
  /**
   * The switch between reading the findings as a list and one at a time. It chooses the shape of
   * what is below, so it belongs on this row rather than up with the run — which is what states
   * which findings these are, and is a different question.
   */
  trailing?: ReactNode;
  /**
   * The role picker, first in the row ahead of the loop switcher, moved down out of the header.
   * Handed down pre-built the same way `trailing` is, so this component stays agnostic of persona
   * data rather than importing `RunSentence` and threading its props through here.
   */
  leading?: ReactNode;
}

/**
 * One row, under the band's edge: which role the findings are voiced as, which loop's run this is,
 * the objective filter that scopes the reading, and the mode switch that shapes it — all on one line
 * (NEX-18040 added the loop switcher to what Briefing #3 already had here; the role picker moved down
 * from the header to sit ahead of it later).
 *
 * The loop switcher and the List/Details picker are fixed width; the objective pills are the one
 * flexible part and absorb whatever room is left, overflowing into a "…" menu rather than wrapping —
 * see `ObjectiveFilterOverflow`. Below `46rem` the row is allowed to wrap instead of crushing the
 * pills to nothing.
 *
 * No rule above this row anymore — the header content it used to separate from is gone. With the
 * role picker and the cog both living down here now, that divider used to mark "everything that acts
 * on the reading starts here"; it mostly separated this row from an empty header instead.
 */
export const NavigationBar = (props: NavigationBarProps) => (
  <div className="mt-2">
    <div className="flex flex-wrap items-center gap-x-4 gap-y-3 max-[46rem]:flex-wrap min-[46rem]:flex-nowrap">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-4 min-[46rem]:flex-nowrap">
        {props.leading}
        {props.leading ? (
          <span
            className="hidden h-4.5 w-px shrink-0 bg-border-neutral-subtle min-[46rem]:inline-block"
            aria-hidden="true"
          />
        ) : null}
        <LoopSwitcher
          scope={props.scope}
          navLoopId={props.navLoopId}
          loops={props.loops}
          runsByLoopId={props.runsByLoopId}
          totalFindings={props.totalLoopFindings}
          onChange={props.onScopeChange}
          onOpenChange={props.onLoopSwitcherOpenChange}
        />
        <span
          className="hidden h-4.5 w-px shrink-0 bg-border-neutral-subtle min-[46rem]:inline-block"
          aria-hidden="true"
        />
        <ObjectiveFilterOverflow
          type={TYPE}
          tone={TONE}
          buckets={props.buckets}
          totalFindings={props.totalFindings}
          objective={props.objective}
          onChange={props.onObjectiveChange}
        />
      </div>
      {props.trailing}
    </div>
  </div>
);
